import { useEffect, useState, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { SELLER } from '@/constants/testIds';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, AlertTriangle, XCircle, Upload, FileText,
  Landmark, Shield, Clock, Eye, EyeOff,
} from 'lucide-react';

const STATUS_META = {
  missing:           { label: 'Pendiente',           icon: Circle,        cls: 'text-slate-400', badge: 'bg-slate-100 text-slate-700 border-slate-200' },
  uploaded:          { label: 'En revisión',         icon: Clock,         cls: 'text-blue-500',  badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  approved:          { label: 'Aprobado',            icon: CheckCircle2,  cls: 'text-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:          { label: 'Rechazado',           icon: XCircle,       cls: 'text-rose-500',  badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  needs_correction:  { label: 'Requiere corrección', icon: AlertTriangle, cls: 'text-amber-500', badge: 'bg-amber-50 text-amber-800 border-amber-200' },
};

export default function SellerOnboarding() {
  const { sellerProfile, refresh } = useAuth();
  const nav = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [banks, setBanks] = useState({ banks: [], account_types: [] });
  const [banking, setBanking] = useState({ bank_name: '', account_type: '', account_number: '', account_holder: '' });
  const [savingBanking, setSavingBanking] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const fileRefs = useRef({});

  useSEO({
    title: 'Activa tu cuenta de vendedor | AutoMarket Colombia',
    description: 'Completa los documentos y la información bancaria para activar tu cuenta de vendedor.',
    canonical: '/vendedor/onboarding',
  });

  const load = async () => {
    const [{ data: st }, { data: b }] = await Promise.all([
      api.get('/sellers/me/onboarding-status'),
      api.get('/config/banks-list'),
    ]);
    setSnapshot(st);
    setBanks(b);
    if (st.banking) {
      setBanking({
        bank_name: st.banking.bank_name || '',
        account_type: st.banking.account_type || '',
        account_number: '', // never re-populate the plaintext number for security
        account_holder: st.banking.account_holder || '',
      });
    }
  };

  useEffect(() => { load(); }, []);

  // Note: unlike the previous version, we DON'T redirect once activation is
  // paid — sellers must be able to re-upload documents if the admin later
  // requests corrections or if they want to add optional files.

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/sellers/documents/${docType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documento cargado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir el documento');
    }
  };

  const saveBanking = async () => {
    if (!banking.bank_name || !banking.account_type || !banking.account_number || !banking.account_holder) {
      toast.error('Completa todos los campos bancarios');
      return;
    }
    setSavingBanking(true);
    try {
      await api.put('/sellers/me/banking', banking);
      toast.success('Información bancaria guardada de forma cifrada');
      setBanking({ ...banking, account_number: '' }); // clear plaintext from state
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingBanking(false);
    }
  };

  if (!snapshot) {
    return <Layout><div className="py-24 text-center text-slate-500">Cargando...</div></Layout>;
  }

  const { documents, labels, required_doc_types, optional_doc_types } = snapshot;
  const allDocTypes = [...required_doc_types, ...optional_doc_types];
  const progressPct = Math.round((snapshot.required_docs_approved / snapshot.required_docs_total) * 100);

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Onboarding vendedor</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
            Activa tu cuenta
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Completa los documentos legales y la información bancaria. El administrador revisará
            cada archivo. Cuando todos estén aprobados deberás elegir una modalidad (Sin plan, Básico o PRO) para empezar a vender.
          </p>
        </div>

        {/* Progreso general */}
        <div data-testid="onboarding-progress" className="mt-6 border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Progreso de verificación</div>
              <div className="mt-1 font-display text-2xl font-black text-slate-900">
                {snapshot.required_docs_approved} / {snapshot.required_docs_total} documentos aprobados
              </div>
            </div>
            <Badge className={`rounded-none ${snapshot.activation_ready ? 'bg-emerald-600' : 'bg-slate-500'}`}>
              {snapshot.activation_ready ? 'Listo para activar' : 'En proceso'}
            </Badge>
          </div>
          <div className="mt-4 h-2 bg-slate-100 overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Checklist de documentos */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-slate-900">Documentos requeridos</h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {allDocTypes.map((docType) => {
              const doc = documents[docType] || { status: 'missing' };
              const meta = STATUS_META[doc.status] || STATUS_META.missing;
              const Icon = meta.icon;
              const isOptional = optional_doc_types.includes(docType);
              return (
                <div
                  key={docType}
                  data-testid={`onboarding-doc-${docType}`}
                  className="border border-slate-200 bg-white p-4 flex items-start gap-3"
                >
                  <Icon className={`h-6 w-6 shrink-0 mt-0.5 ${meta.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{labels[docType]}</div>
                      {isOptional && <span className="text-[10px] text-slate-500">(opcional)</span>}
                      <Badge className={`rounded-none border ${meta.badge}`} variant="outline">
                        {meta.label}
                      </Badge>
                    </div>
                    {doc.admin_notes && (
                      <div className="mt-1 text-xs text-slate-600 bg-slate-50 border border-slate-200 p-2">
                        <strong>Nota del admin:</strong> {doc.admin_notes}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                      {doc.url && doc.status !== 'missing' && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                        >
                          <FileText className="h-3.5 w-3.5" /> Ver archivo
                        </a>
                      )}
                      <label className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 hover:text-orange-600 cursor-pointer">
                        <Upload className="h-3.5 w-3.5" />
                        {doc.status === 'missing' ? 'Cargar documento' : 'Reemplazar'}
                        <input
                          ref={(el) => (fileRefs.current[docType] = el)}
                          data-testid={`onboarding-upload-${docType}`}
                          type="file"
                          className="hidden"
                          accept="application/pdf,image/*"
                          onChange={(e) => uploadDoc(docType, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Información bancaria */}
        <div className="mt-8">
          <div className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900">Información bancaria</h2>
            <Badge variant="outline" className="rounded-none border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">
              <Shield className="h-3 w-3 mr-1" /> Cifrada en reposo
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            El número de cuenta se guarda cifrado con AES. Solo el administrador con permiso puede consultarlo para gestionar tus desembolsos.
          </p>
          <div className="mt-3 border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Banco</label>
              <Select value={banking.bank_name} onValueChange={(v) => setBanking({ ...banking, bank_name: v })}>
                <SelectTrigger data-testid="onboarding-bank-name" className="rounded-none mt-1"><SelectValue placeholder="Selecciona el banco" /></SelectTrigger>
                <SelectContent>
                  {banks.banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Tipo de cuenta</label>
              <Select value={banking.account_type} onValueChange={(v) => setBanking({ ...banking, account_type: v })}>
                <SelectTrigger data-testid="onboarding-account-type" className="rounded-none mt-1"><SelectValue placeholder="Ahorros / Corriente" /></SelectTrigger>
                <SelectContent>
                  {banks.account_types.map((t) => <SelectItem key={t} value={t}>{t === 'ahorros' ? 'Ahorros' : 'Corriente'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Número de cuenta</label>
              <div className="mt-1 relative">
                <Input
                  data-testid="onboarding-account-number"
                  type={showAccountNumber ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={snapshot.banking?.account_number_masked || 'Solo dígitos (8-20)'}
                  className="rounded-none pr-10 font-mono"
                  value={banking.account_number}
                  onChange={(e) => setBanking({ ...banking, account_number: e.target.value.replace(/\D/g, '') })}
                />
                <button
                  type="button"
                  data-testid="onboarding-account-number-toggle"
                  onClick={() => setShowAccountNumber((v) => !v)}
                  aria-label={showAccountNumber ? 'Ocultar número' : 'Mostrar número'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900"
                >
                  {showAccountNumber ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {snapshot.banking?.has_account_number && !banking.account_number && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Guardado: {snapshot.banking.account_number_masked}. Escribe uno nuevo para reemplazarlo.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Titular de la cuenta</label>
              <Input
                data-testid="onboarding-account-holder"
                className="rounded-none mt-1"
                value={banking.account_holder}
                onChange={(e) => setBanking({ ...banking, account_holder: e.target.value })}
                placeholder="Nombre o razón social"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                data-testid="onboarding-save-banking"
                onClick={saveBanking}
                disabled={savingBanking}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                {savingBanking ? 'Guardando...' : 'Guardar información bancaria'}
              </Button>
            </div>
          </div>
        </div>

        {/* Estado final */}
        <div className="mt-8 border-l-4 border-orange-500 bg-orange-50 p-4 text-sm text-slate-700">
          {snapshot.activation_ready ? (
            <>
              <strong>Todos los documentos fueron aprobados.</strong> Elige tu modalidad
              desde <a className="underline font-semibold" href="/vendedor/suscripcion">Modalidad y plan</a> para empezar a vender.
            </>
          ) : (
            <>
              <strong>No podrás vender</strong> hasta que el administrador
              verifique y apruebe todos los documentos requeridos. Recibirás una notificación por
              cada estado.
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
